<template>
  <div
    :class="
      `flex items-center  ${
        countChar > 1000
          ? 'border-b-0 border border-gray-300'
          : 'border border-gray-300'
      } min-h-[100px] pt-1`
    "
  >
    <label
      class="cursor-pointer ml-2 text-xl hover:text-red-lp-100 transition items-center my-3"
      @click="showEmojiPicker"
    >
      <img
        src="@/app/ui/assets/svg/emoji-chat.svg"
        class="w-5 h-5"
        alt="emoji"
      />
    </label>
    <!-- Editor -->
    <div
      ref="editorRef"
      id="ql-editor"
      class="ql-editor flex-1 overflow-y-auto outline-none"
      style="max-height: 12.5em;"
    ></div>

    <!-- Upload -->
    <label
      class="cursor-pointer ml-2 text-xl hover:text-red-lp-100 transition items-center mr-2 my-3"
    >
      <img
        src="@/app/ui/assets/svg/attachment-chat.svg"
        class="w-5 h-5"
        alt="attachment"
      />
      <input
        type="file"
        class="hidden"
        @change="handleFileUpload"
        :accept="acceptContentFlag"
        multiple
      />
    </label>
    <label
      v-if="isSendButton"
      class="cursor-pointer ml-2 text-xl hover:text-red-lp-100 transition items-center mr-4 my-3"
    >
      <img
        v-if="isDisableSend"
        src="@/app/ui/assets/svg/send-chat.svg"
        class="w-5 h-5"
        alt="send"
      />
      <img
        v-else
        src="@/app/ui/assets/svg/send-chat-active.svg"
        class="w-5 h-5"
        alt="send"
        @click="sendChat"
      />
    </label>
  </div>
  <div
    v-if="countChar > 1000"
    class="flex text-red-lp-100 text-sm pl-6 gap-2 border border-t-0 border-gray-300 p-2"
  >
    <img
      src="@/app/ui/assets/svg/warning-red.svg"
      alt="trash"
      class="w-5 h-5"
    />
    <p>{{ $t('pusatResolusi.chatBox.maxChar') }}</p>
  </div>

  <!-- Custom Toolbar -->
  <div
    ref="toolbarRef"
    class="flex flex-wrap items-center rounded-b-lg gap-2 pt-3"
  >
    <select class="ql-color" />
    <button class="ql-bold" />
    <button class="ql-italic" />
    <button class="ql-underline" />
    <button class="ql-strike" />
    <button class="ql-list" value="bullet" />
    <button class="ql-list" value="ordered" />
    <button class="ql-emoji" style="display: none;" />
  </div>
</template>

<script>
import Quill from "quill";
import "quill/dist/quill.snow.css";
import "quill-emoji/dist/quill-emoji.css";
import emojiMap from "./emoji-map";
import * as Emoji from "quill-emoji";
import MagicUrl from "quill-magic-url";
import { FlagsPermissionPusatResolusiV2 } from "@/feature-flags/flags-pusat-resolusi-v2";

export default {
  emits: ["update:modelValue", "text-change", "file-upload", "submit"],
  props: {
    modelValue: String,
    placeholder: String,
    readonly: Boolean,
    formats: Array,
    maxLength: Number,
    isMaxLength: Boolean,
    isSendButton: Boolean,
    isDisableSend: Boolean,
    countChar: Number
  },
  quill: null,
  computed: {
    filledValueLength() {
      return this.modelValue.replace(/(<([^>]+)>)/gi, "").length;
    },
    acceptContentFlag() {
      return FlagsPermissionPusatResolusiV2.config_accept_attachment_extension.getValue();
    }
  },
  watch: {
    modelValue(newVal) {
      if (!this.quill) return;

      const currentHtml = this.quill.root.innerHTML;
      const conditon =
        (!newVal && currentHtml === "<p><br></p>") || newVal === currentHtml;

      // Skip update if Quill already contains the new value
      if (conditon) {
        return;
      }
      this.quill.root.innerHTML = newVal || "<p><br></p>";
      const currentSelection = this.quill.getSelection();
      const index = currentSelection?.index || 0;
      this.quill.setSelection(index + newVal.length, 0, "silent");
    }
  },
  mounted() {
    const CustomEmojiBlot = this.initializeEmojiBlot();
    const TemplateBlot = this.initializeTemplateBlot();

    // Register components
    Quill.register({
      "formats/emoji": CustomEmojiBlot,
      "formats/template": TemplateBlot,
      "modules/emoji": Emoji,
      "modules/magicUrl": MagicUrl
    });
    const editorEl = this.$refs.editorRef;
    const toolbarEl = this.$refs.toolbarRef;

    if (!editorEl || !toolbarEl) return;

    this.quill = new Quill(editorEl, {
      theme: "snow",
      placeholder: this.$t('pusatResolusi.success.insertYourMessage'),
      modules: {
        toolbar: toolbarEl,
        "emoji-toolbar": true,
        "emoji-shortname": true,
        magicUrl: {
          urlRegularExpression: /(https?:\/\/[\S]+)|(www.[\S]+)|(tel:[\S]+)/g,
          globalRegularExpression: /(https?:\/\/|www\.|tel:)[\S]+/g
        }
      }
    });

    this.renderValue(this.modelValue);

    this.quill.on("text-change", (delta, oldContents, source) => {
      if (source === "user") {
        let html = this.$refs.editorRef.children[0].innerHTML;
        const text = this.quill.getText().trim();
        if (html === "<p><br></p>") {
          html = "";
        }

        this.$emit("update:modelValue", html);
        this.$emit("text-change", {
          htmlValue: html,
          textValue: text,
          delta: delta,
          source: source
        });
        this.scrollToBottom();
      }
    });
  },
  methods: {
    renderValue(value) {
      if (this.quill) {
        if (value) this.quill.pasteHTML(value);
        else this.quill.setText("");
      }
    },
    async showEmojiPicker() {
      if (!this.quill) return;
      const toolbar = document.querySelector(".ql-toolbar");
      const emojiBtn = toolbar?.querySelector(".ql-emoji");
      if (emojiBtn) {
        emojiBtn.click(); // Programmatically trigger the original button
      }
      await this.$nextTick();
      const emojiOverlay = document.getElementById("emoji-close-div");
      const toolbarContainer = document.getElementById("ql-editor");
      if (emojiOverlay) {
        toolbarContainer.append(emojiOverlay);
      }
    },
    handleFileUpload(event) {
      this.$emit("file-upload", event);
    },
    sendChat() {
      this.$emit("submit");
    },
    initializeEmojiBlot() {
      const EmojiBlot = Quill.import("blots/embed");
      class CustomEmojiBlot extends EmojiBlot {
        static create(value) {
          const node = super.create();
          const parseUnicode = (unicode) => {
            return unicode.split("-").map((str) => parseInt(str, 16));
          };

          if (typeof value === "object") {
            node.innerText = String.fromCodePoint(parseUnicode(value.unicode));
          } else if (typeof value === "string") {
            const valueObj = emojiMap[value];
            if (valueObj) {
              node.innerText = String.fromCodePoint(
                parseUnicode(valueObj.unicode)
              );
            }
          }
          return node;
        }
        static value(node) {
          return node.innerText;
        }
        static blotName = "emoji";
        static className = "emoji";
        static tagName = "span";
      }
      return CustomEmojiBlot;
    },
    initializeTemplateBlot() {
      const Inline = Quill.import("blots/inline");
      class TemplateBlot extends Inline {
        static create(value) {
          const node = super.create();
          node.setAttribute("data-template", value);
          node.innerText = value;
          node.classList.add("template-chip");
          return node;
        }

        static value(node) {
          return node.getAttribute("data-template") || "";
        }
      }
      TemplateBlot.blotName = "template";
      TemplateBlot.tagName = "span";
      TemplateBlot.className = "template-inserted";
      return TemplateBlot;
    },
    scrollToBottom() {
      if (!this.quill) return;
      const editor = this.quill.root;
      editor.scrollTop = editor.scrollHeight;
    }
  },
  beforeUnmount() {
    this.quill = null;
  }
};
</script>
<style scoped>
/* Remove border & padding from Quill's inner editor */
.ql-container {
  border: none !important;
}
.ql-editor {
  padding: 0 !important;
  border: none !important;
}
::v-deep(.ql-picker-options) {
  bottom: 30px !important;
  top: unset !important;
}
::v-deep(.ql-toolbar button:hover .ql-stroke),
::v-deep(.ql-toolbar button.ql-active .ql-stroke),
::v-deep(.ql-toolbar button:hover .ql-fill),
::v-deep(.ql-toolbar button.ql-active .ql-fill),
::v-deep(.ql-toolbar button:hover .ql-stroke-miter),
::v-deep(.ql-toolbar button.ql-active .ql-stroke-miter),
::v-deep(.ql-toolbar select.ql-picker-label.ql-active),
::v-deep(.ql-toolbar select.ql-picker-label.ql-hover) {
  stroke: #b82025 !important;
  color: #b82025 !important;
}
:deep(#emoji-palette) {
  position: fixed !important;
  left: 260px !important;
  top: auto !important;
  bottom: 180px !important;
  box-shadow: none;
  border: none;
  max-width: 380px;
}
:deep(#tab-panel) {
  gap: 10px;
}
:deep(.emoji-tab.active) {
  transform: none;
}
:deep(#tab-toolbar .active) {
  border-bottom: #b82025 solid 3px;
}

#tab-toolbar ul li {
  list-style: none;
  padding-left: 0;
  margin: 0;
}
.scroll-smooth {
  -ms-overflow-style: none; /* Internet Explorer dan Edge */
  scrollbar-width: none; /* Firefox */
}

.scroll-smooth::-webkit-scrollbar {
  display: none; /* Chrome, Safari dan Opera */
}
</style>
